/**
 * TemplatePreview — renders a template's `contentPreview` with `{{n}}`
 * placeholders highlighted.
 *
 * We resolve each `{{n}}` token against the variables array in two ways:
 *   1. Prefer `example` if present.
 *   2. Fall back to the variable `name` wrapped in braces (e.g. `{customer_name}`).
 *
 * Unknown positions render as-is (`{{?}}`) so the operator spots missing
 * variable definitions. Output is a span-wrapped sequence for safe styling.
 */

import type { TemplateVariable } from '@/components/templates/VariablesEditor';

interface Props {
  content: string | null;
  variables: TemplateVariable[];
}

type Segment = { kind: 'text'; value: string } | { kind: 'var'; label: string; missing: boolean };

function buildSegments(content: string, variables: TemplateVariable[]): Segment[] {
  const byPosition = new Map<number, TemplateVariable>();
  for (const v of variables) byPosition.set(v.position, v);

  const re = /\{\{(\d+)\}\}/g;
  const segments: Segment[] = [];
  let lastIdx = 0;
  for (const m of content.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > lastIdx) {
      segments.push({ kind: 'text', value: content.slice(lastIdx, start) });
    }
    const positionStr = m[1] ?? '';
    const position = Number(positionStr);
    const found = byPosition.get(position);
    if (found) {
      const label = found.example && found.example.length > 0 ? found.example : `{${found.name}}`;
      segments.push({ kind: 'var', label, missing: false });
    } else {
      segments.push({ kind: 'var', label: `{{${positionStr || '?'}}}`, missing: true });
    }
    lastIdx = start + m[0].length;
  }
  if (lastIdx < content.length) {
    segments.push({ kind: 'text', value: content.slice(lastIdx) });
  }
  return segments;
}

export function TemplatePreview({ content, variables }: Props) {
  if (!content || content.trim() === '') {
    return (
      <div className="rounded-md border border-dashed bg-muted/40 px-3 py-4 text-xs text-muted-foreground">
        Sem conteúdo para pré-visualizar.
      </div>
    );
  }

  const segments = buildSegments(content, variables);
  return (
    <div
      data-testid="template-preview"
      className="whitespace-pre-wrap rounded-md border bg-muted/40 px-3 py-2 text-sm"
    >
      {segments.map((seg, idx) => {
        if (seg.kind === 'text') {
          return <span key={idx}>{seg.value}</span>;
        }
        return (
          <span
            key={idx}
            data-testid={seg.missing ? 'preview-missing' : 'preview-var'}
            className={
              seg.missing
                ? 'rounded bg-destructive/20 px-1 py-0.5 font-mono text-xs text-destructive'
                : 'rounded bg-primary/15 px-1 py-0.5 font-mono text-xs text-primary'
            }
          >
            {seg.label}
          </span>
        );
      })}
    </div>
  );
}
